import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as tus from "tus-js-client";
import { api } from "../lib/api";
import { GENERATION_LANGS, languageName } from "../lib/languages";

type Stage = "idle" | "preparing" | "uploading" | "done" | "error";

export function Upload() {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [sourceLang, setSourceLang] = useState<string>("de");
  const [stage, setStage] = useState<Stage>("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uid, setUid] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<tus.Upload | null>(null);
  const navigate = useNavigate();

  function onFileChosen(f: File | null) {
    setFile(f);
    if (f && !title) setTitle(stripExt(f.name));
  }

  async function start() {
    if (!file) return;
    setStage("preparing");
    setError(null);
    setProgress(0);

    try {
      const minted = await api.mintUploadUrl(title || file.name, file.size);
      setUid(minted.uid ?? null);

      const upload = new tus.Upload(file, {
        endpoint: minted.uploadURL,
        uploadUrl: minted.uploadURL, // creation already done on server
        retryDelays: [0, 1000, 3000, 5000, 10000, 20000],
        chunkSize: 50 * 1024 * 1024, // Cloudflare requires chunks ≥ 5 MB, ≤ 200 MB
        metadata: { name: title || file.name, filetype: file.type },
        onError: (e) => {
          setError(e.message);
          setStage("error");
        },
        onProgress: (sent, total) => setProgress((sent / total) * 100),
        onSuccess: async () => {
          setProgress(100);
          setStage("done");
          // Persist title + source language. source_lang gates the
          // generation panel on the video detail page.
          if (minted.uid) {
            try {
              await api.updateVideo(minted.uid, {
                name: title || file.name,
                meta: { source_lang: sourceLang },
              });
            } catch { /* ignore */ }
          }
        },
      });

      uploadRef.current = upload;
      setStage("uploading");
      upload.start();
    } catch (e) {
      setError((e as Error).message);
      setStage("error");
    }
  }

  function cancel() {
    uploadRef.current?.abort();
    setStage("idle");
    setProgress(0);
  }

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl font-semibold">Upload video</h1>

      <div
        className={`card p-8 text-center cursor-pointer transition-colors ${
          file ? "border-neutral-400" : "hover:border-neutral-400 border-dashed"
        }`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          if (stage === "uploading" || stage === "preparing") return;
          const f = e.dataTransfer.files?.[0];
          if (f) onFileChosen(f);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => onFileChosen(e.target.files?.[0] ?? null)}
          disabled={stage === "uploading" || stage === "preparing"}
        />
        {file ? (
          <div>
            <div className="font-medium">{file.name}</div>
            <div className="text-sm text-neutral-500 mt-1">{formatBytes(file.size)}</div>
          </div>
        ) : (
          <div className="text-neutral-500">
            <div className="text-sm">Drop a .mp4 here, or click to choose</div>
            <div className="text-xs mt-1">Up to 30 GB · resumes automatically on network drop</div>
          </div>
        )}
      </div>

      <div>
        <label className="label">Title</label>
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Keynote — Q3 2026"
          disabled={stage === "uploading" || stage === "preparing"}
        />
      </div>

      <div>
        <label className="label">Source language</label>
        <select
          className="input"
          value={sourceLang}
          onChange={(e) => setSourceLang(e.target.value)}
          disabled={stage === "uploading" || stage === "preparing"}
        >
          {GENERATION_LANGS.map((code) => (
            <option key={code} value={code}>{languageName(code)} — {code}</option>
          ))}
        </select>
        <p className="text-xs text-neutral-500 mt-1">
          The language spoken in this video. Used as the source for auto-generated dubs and subtitles.
        </p>
      </div>

      {(stage === "uploading" || stage === "preparing") && (
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span>{stage === "preparing" ? "Preparing upload…" : `Uploading… ${progress.toFixed(1)}%`}</span>
            <button onClick={cancel} className="text-xs text-neutral-500 hover:text-red-600">Cancel</button>
          </div>
          <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
            <div className="h-full bg-neutral-900 transition-[width] duration-150" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {stage === "error" && error && (
        <div className="card p-4 border-red-300 bg-red-50 text-red-800 text-sm">{error}</div>
      )}

      {stage === "done" && (
        <div className="card p-4 border-green-300 bg-green-50 text-green-800 text-sm space-y-2">
          <div>Upload complete. Cloudflare is now encoding the video — it may take a few minutes before it's playable.</div>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => navigate(`/videos/${uid}`)}>Open video</button>
            <button className="btn-secondary" onClick={() => navigate("/")}>Back to library</button>
          </div>
        </div>
      )}

      {stage === "idle" && (
        <button className="btn-primary" disabled={!file} onClick={start}>
          Start upload
        </button>
      )}
    </div>
  );
}

function stripExt(name: string) {
  return name.replace(/\.[^/.]+$/, "");
}

function formatBytes(n: number) {
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}
